import type {PropertyDefinition} from '@github-ui/custom-properties-types'
import {Link} from '@github-ui/react-router'
import {Card} from '@primer/react/experimental'

import {useNewPropertyPath} from '../hooks/use-property-details-paths'
import styles from './DefinitionIceBreakers.module.css'

interface DefinitionIceBreakerProps {
  title: string
  description: string
  suggestedDefinition: Omit<PropertyDefinition, 'source'>
}

const suggestions: DefinitionIceBreakerProps[] = [
  {
    title: 'Create a deployment property',
    description: 'Is this a production repository?',
    suggestedDefinition: {
      propertyName: 'deploys_to_production',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description:
        'Indicates whether the indicated repository deploys to a production environment, or rather a staging or sandbox environment, for example.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    title: 'Create a team property',
    description: 'Who owns this repository?',
    suggestedDefinition: {
      propertyName: 'owning_team',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description: 'The team that owns this repository.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    title: 'Create an environment property',
    description: 'What compliance standards apply?',
    suggestedDefinition: {
      propertyName: 'compliance_tier',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description:
        'The compliance standards that apply to this repository, which influence how its data must be handled.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
]

export function DefinitionIceBreakers() {
  return (
    <div className="flex-1 px-3">
      <h3 className="h4 mb-4">Get started with custom properties</h3>
      <div className="d-flex gap-3 flex-wrap">
        {suggestions.map(suggestion => (
          <DefinitionIceBreaker key={suggestion.title} {...suggestion} />
        ))}
      </div>
    </div>
  )
}

function DefinitionIceBreaker({title, description, suggestedDefinition}: DefinitionIceBreakerProps) {
  const newPropertyPath = useNewPropertyPath()

  return (
    <Link to={newPropertyPath} state={{suggestedDefinition}} className={styles.Link}>
      <Card className={styles.Card} data-testid="definition-ice-breaker">
        <Card.Heading as="h4" className={styles.CardHeading}>
          {title}
        </Card.Heading>
        <Card.Description>{description}</Card.Description>
      </Card>
    </Link>
  )
}
